import { useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { type WithApolloContext } from 'modules/Apollo/types';
import { extensionAppState } from 'hooks/useExtensionState';
import { activeWorkspaceLayoutPresetState } from './state';
import { useWorkspaceLayoutManager } from './useWorkspaceLayoutManager';
import { useWorkspaceLayoutPresetToApply } from './useWorkspaceLayoutPresetToApply';
import { useIsWorkspaceLayoutOwner } from './useIsWorkspaceLayoutOwner';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { logger } from 'modules/logger';

const getWindowsSignature = (
  windows: typeof extensionAppState extends never ? never : Record<string, any>
) =>
  Object.entries(windows)
    .filter(([, tab]) => tab != null)
    .map(([name, tab]) => `${name}:${tab.id}:${tab.path}`)
    .sort()
    .join('|');

export function useAutoApplyWorkspaceLayoutPreset({ context }: WithApolloContext): void {
  const [isWorkspaceLayoutV2Enabled] = useFeatureFlagEnabled(FF.WORKSPACE_LAYOUT_V2);
  const isOwner = useIsWorkspaceLayoutOwner({ isEnabled: isWorkspaceLayoutV2Enabled });

  const extensionState = useRecoilValue(extensionAppState);
  const activeLayoutPreset = useRecoilValue(activeWorkspaceLayoutPresetState);
  const setActiveLayoutPreset = useSetRecoilState(activeWorkspaceLayoutPresetState);
  const { applyWorkspaceLayoutPreset } = useWorkspaceLayoutManager();
  const lastApplyRef = useRef<{
    presetId: string;
    windowsSignature: string;
    requestedAt?: number;
  } | null>(null);
  const {
    presetToApply,
    presetToApplyReason,
    currentArrangementKey,
    pickedOnArrangementKey,
    isActivePresetMissing,
  } = useWorkspaceLayoutPresetToApply({ context });
  const { refetch: refetchCurrentUser } = useCurrentUser({ context });
  const missingActivePresetId = isActivePresetMissing ? activeLayoutPreset?.presetId : undefined;

  const windowsSignature = useMemo(
    () => getWindowsSignature(extensionState.windows as Record<string, any>),
    [extensionState.windows]
  );

  const lastArrangementKeyRef = useRef(currentArrangementKey);

  useEffect(() => {
    // Displays changed (e.g. a monitor slept and came back), so apply again on the next match
    if (lastArrangementKeyRef.current !== currentArrangementKey) {
      lastArrangementKeyRef.current = currentArrangementKey;
      lastApplyRef.current = null;
    }

    if (
      !isWorkspaceLayoutV2Enabled ||
      !isOwner ||
      presetToApply == null ||
      windowsSignature.length === 0 ||
      presetToApply.windows.length === 0
    ) {
      return;
    }

    // A fresh pick of the same layout asks for it to be applied again
    const requestedAt =
      activeLayoutPreset?.presetId === presetToApply.id
        ? activeLayoutPreset.requestedAt
        : undefined;
    const alreadyApplied =
      lastApplyRef.current?.presetId === presetToApply.id &&
      lastApplyRef.current?.windowsSignature === windowsSignature &&
      lastApplyRef.current?.requestedAt === requestedAt;
    if (alreadyApplied) return;

    const appliedRequest = { presetId: presetToApply.id, windowsSignature, requestedAt };
    lastApplyRef.current = appliedRequest;
    // Says why this layout won, e.g. a monitor setup change bringing its own default
    logger.info('[WorkspaceLayoutManager] Auto-applying layout', {
      presetId: presetToApply.id,
      reason: presetToApplyReason,
      arrangementKey: currentArrangementKey,
      pickedOnArrangementKey,
    });
    void applyWorkspaceLayoutPreset(presetToApply).then((didApply) => {
      // Not sent (e.g. the extension didn't answer), so let the next change try again
      if (!didApply && lastApplyRef.current === appliedRequest) lastApplyRef.current = null;
    });
  }, [
    activeLayoutPreset,
    applyWorkspaceLayoutPreset,
    currentArrangementKey,
    isOwner,
    isWorkspaceLayoutV2Enabled,
    pickedOnArrangementKey,
    presetToApply,
    presetToApplyReason,
    windowsSignature,
  ]);

  // The selected layout is missing here: check a fresh list, and if it's really gone (deleted on
  // another workstation, or a missed broadcast) clear it so the default takes over
  useEffect(() => {
    if (!isWorkspaceLayoutV2Enabled || !isOwner || missingActivePresetId == null) return;

    let isCurrent = true;
    refetchCurrentUser()
      .then(({ data }) => {
        const presets = data?.me?.workspaceSettings?.layoutSettings?.presets ?? [];
        if (!isCurrent || presets.some((preset) => preset.id === missingActivePresetId)) return;

        logger.info(
          '[WorkspaceLayoutManager] Selected layout no longer exists, using the default',
          {
            presetId: missingActivePresetId,
          }
        );
        setActiveLayoutPreset((active) =>
          active?.presetId === missingActivePresetId ? null : active
        );
      })
      .catch((error: unknown) => {
        logger.warn(
          '[WorkspaceLayoutManager] Could not refresh layouts for a missing selection',
          error
        );
      });

    return () => {
      isCurrent = false;
    };
  }, [
    isOwner,
    isWorkspaceLayoutV2Enabled,
    missingActivePresetId,
    refetchCurrentUser,
    setActiveLayoutPreset,
  ]);
}
